/**
 * FERP Portal - Approval List Page (DRR-074: Tabulator DRY Template)
 *
 * EXECUTE Phase: Life-critical approval requests for patient care, financial, and compliance.
 * Delegates to TabulatorPrimitiveManager for consistent CRUD across all 25 primitives.
 *
 * Supports "All Approvals" (tenant-wide, scope-gated) and "My Approvals"
 * (backend-filtered via Valkey O(1) index by approver actor ID).
 */

import { useState, useMemo } from 'react';
import { SegmentedControl, Group, Badge, Loader, Text, Stack } from '@mantine/core';
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';
import { useDevActorId } from '@/hooks/useDevActorId';
import { useTenantId } from '@/auth/AuthContext';
import { useListApprovalsForApproverQuery } from '@/generated/graphql';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

type ApprovalFilter = 'all' | 'my';

export function ApprovalListPage() {
  const [filter, setFilter] = useState<ApprovalFilter>('all');
  const { actorId, actorName, loading: actorLoading } = useDevActorId();
  const tenantId = useTenantId() ?? '';

  // Backend-filtered query: listApprovalsForApprover (Valkey O(1) index)
  const {
    data: myApprovalsData,
    loading: myApprovalsLoading,
    error: myApprovalsError,
    refetch: myApprovalsRefetch,
  } = useListApprovalsForApproverQuery({
    variables: { tenantId, approverId: actorId ?? '', limit: 100 },
    skip: filter !== 'my' || !actorId || !tenantId,
  });

  // Override data for TabulatorPrimitiveManager when "My Approvals" is active
  const myApprovalsOverride = useMemo(() => {
    if (filter !== 'my') return undefined;
    return {
      items: (myApprovalsData?.listApprovalsForApprover?.items ?? null) as PrimitiveItem[] | null,
      loading: myApprovalsLoading || actorLoading,
      error: myApprovalsError,
      refetch: myApprovalsRefetch,
    };
  }, [filter, myApprovalsData, myApprovalsLoading, myApprovalsError, myApprovalsRefetch, actorLoading]);

  const headerSlot = useMemo(
    () => (
      <Stack gap="sm" mb="sm">
        <Group gap="md">
          <SegmentedControl
            size="xs"
            value={filter}
            onChange={(value) => setFilter(value as ApprovalFilter)}
            data={[
              { label: 'All Approvals', value: 'all' },
              { label: 'My Approvals', value: 'my' },
            ]}
          />
          {filter === 'my' && (
            <Group gap="xs">
              {actorLoading ? (
                <Loader size="xs" />
              ) : actorId ? (
                <Badge variant="light" color="blue" size="sm">
                  {actorName}
                </Badge>
              ) : (
                <Text size="xs" c="dimmed">
                  No actor found for current user
                </Text>
              )}
            </Group>
          )}
        </Group>
      </Stack>
    ),
    [filter, actorId, actorName, actorLoading]
  );

  return (
    <TabulatorPrimitiveManager
      primitiveType="approval"
      headerSlot={headerSlot}
      overrideData={myApprovalsOverride}
    />
  );
}

export default ApprovalListPage;
